import { enumMembers, type ApiSchema } from "../apiSchema";
import { readArray, readObject, readOneOf, readString } from "../http/jsonDecoding";

/**
 * A seed's imported review history as the review read sends it (`ImportedReviewBody` in
 * `server/src/dmw/api/routes/content/reviews/review_bodies.py`). It keeps the source's
 * attribution and grants no local approval. Only administrators get emails and mappings.
 */

export type ActorMapping = ApiSchema<"ActorMapping">;
type HistoricalDecisionKind = ApiSchema<"ReviewDecisionKind">;

const ACTOR_MAPPINGS = enumMembers<ActorMapping>({ matched: true, invited: true, unmatched: true });
const HISTORICAL_DECISION_KINDS = enumMembers<HistoricalDecisionKind>({
  approve: true,
  request_changes: true,
  scrap: true,
  merge: true,
});

export interface HistoricalActor {
  readonly displayName: string;
  readonly email: string | null;
  readonly mapping: ActorMapping | null;
}

export interface HistoricalDecision {
  readonly decisionId: string;
  readonly kind: HistoricalDecisionKind;
  readonly reviewer: HistoricalActor;
  readonly reviewedHead: string;
  readonly comment: string;
  readonly at: string;
}

export interface HistoricalReview {
  readonly author: HistoricalActor;
  readonly decisions: readonly HistoricalDecision[];
}

function readActor(value: unknown): HistoricalActor {
  const actor = readObject(value, "historical actor");
  return {
    displayName: readString(actor, "display_name"),
    email: actor["email"] == null ? null : readString(actor, "email"),
    mapping: actor["mapping"] == null ? null : readOneOf(actor, "mapping", ACTOR_MAPPINGS),
  };
}

function readDecision(value: unknown): HistoricalDecision {
  const decision = readObject(value, "historical decision");
  return {
    decisionId: readString(decision, "decision_id"),
    kind: readOneOf(decision, "kind", HISTORICAL_DECISION_KINDS),
    reviewer: readActor(decision["reviewer"]),
    reviewedHead: readString(decision, "reviewed_head"),
    comment: readString(decision, "comment"),
    at: readString(decision, "at"),
  };
}

export function readHistoricalReview(value: unknown): HistoricalReview | null {
  if (value == null) return null;
  const history = readObject(value, "historical review");
  return {
    author: readActor(history["author"]),
    decisions: readArray(history["decisions"], "historical decisions", readDecision),
  };
}
